/**
 * Do-Calculus and Intervention Analysis - Phase 12 Sprint 6
 *
 * Implements Pearl's do-calculus for causal inference:
 * - Graph manipulation for interventions
 * - Causal effect identifiability
 * - Adjustment formula generation
 * - Frontdoor and backdoor criteria
 */

import type {
  CausalGraph,
  GraphEdge,
  Intervention,
  InterventionResult,
  InterventionRequest,
  AdjustmentFormula,
} from "../types.js";
import {
  checkDSeparation,
  findBackdoorAdjustmentSet,
  isValidBackdoorAdjustment,
} from "./d-separation.js";

// ============================================================================
// GRAPH MANIPULATION
// ============================================================================

/**
 * Create a mutilated graph by removing incoming edges to intervened variables
 */
export function createMutilatedGraph(
  graph: CausalGraph,
  interventions: Intervention[],
): CausalGraph {
  const intervenedVars = new Set(interventions.map((i) => i.variable));

  // Remove all incoming edges to intervened variables
  const newEdges = graph.edges.filter((edge) => !intervenedVars.has(edge.to));

  return {
    ...graph,
    id: `${graph.id}_mutilated`,
    edges: newEdges,
    metadata: {
      ...graph.metadata,
      description: `Mutilated graph with interventions on: ${Array.from(intervenedVars).join(", ")}`,
    },
  };
}

/**
 * Create a graph with a variable removed (for marginalization)
 */
export function createMarginalizedGraph(
  graph: CausalGraph,
  variableToRemove: string,
): CausalGraph {
  const newNodes = graph.nodes.filter((n) => n.id !== variableToRemove);
  const newEdges: GraphEdge[] = [];

  // Find parents and children of the removed variable
  const parents: string[] = [];
  const children: string[] = [];

  for (const edge of graph.edges) {
    if (edge.to === variableToRemove) {
      parents.push(edge.from);
    } else if (edge.from === variableToRemove) {
      children.push(edge.to);
    } else {
      newEdges.push(edge);
    }
  }

  // Connect parents directly to children (transitive closure)
  for (const parent of parents) {
    for (const child of children) {
      // Check if edge doesn't already exist
      const exists = newEdges.some((e) => e.from === parent && e.to === child);
      if (!exists) {
        newEdges.push({
          from: parent,
          to: child,
          type: "directed",
        });
      }
    }
  }

  return {
    ...graph,
    id: `${graph.id}_marginalized_${variableToRemove}`,
    nodes: newNodes,
    edges: newEdges,
  };
}

// ============================================================================
// CAUSAL EFFECT IDENTIFIABILITY
// ============================================================================

/**
 * Check if causal effect P(Y|do(X)) is identifiable from observational data
 */
export function isIdentifiable(
  graph: CausalGraph,
  treatment: string,
  outcome: string,
): { identifiable: boolean; reason: string; method?: string } {
  // Check for backdoor criterion
  const backdoorSet = findBackdoorAdjustmentSet(graph, treatment, outcome);
  if (backdoorSet !== null) {
    return {
      identifiable: true,
      reason: "Backdoor criterion satisfied",
      method: "backdoor",
    };
  }

  // Check for frontdoor criterion
  const frontdoorResult = checkFrontdoorCriterion(graph, treatment, outcome);
  if (frontdoorResult.satisfied) {
    return {
      identifiable: true,
      reason: "Frontdoor criterion satisfied",
      method: "frontdoor",
    };
  }

  // Check for instrumental variables
  const instrumentalResult = findInstrumentalVariable(
    graph,
    treatment,
    outcome,
  );
  if (instrumentalResult !== null) {
    return {
      identifiable: true,
      reason: "Instrumental variable available",
      method: "instrumental",
    };
  }

  // Check for general identifiability using do-calculus rules
  // (simplified check - full algorithm is complex)
  const generalResult = checkGeneralIdentifiability(graph, treatment, outcome);
  if (generalResult.identifiable) {
    return {
      identifiable: true,
      reason: "Identifiable via do-calculus",
      method: "general",
    };
  }

  return {
    identifiable: false,
    reason:
      "No valid adjustment set found and frontdoor criterion not satisfied",
  };
}

// ============================================================================
// BACKDOOR CRITERION
// ============================================================================

/**
 * Find all valid backdoor adjustment sets
 */
export function findAllBackdoorSets(
  graph: CausalGraph,
  treatment: string,
  outcome: string,
  maxSize = 5,
): string[][] {
  const treatmentDescendants = getDescendants(graph, treatment);
  const candidates = graph.nodes
    .map((n) => n.id)
    .filter(
      (id) =>
        id !== treatment && id !== outcome && !treatmentDescendants.has(id),
    );

  const validSets: string[][] = [];

  // Generate all subsets up to maxSize
  for (let size = 0; size <= Math.min(maxSize, candidates.length); size++) {
    for (const subset of getCombinations(candidates, size)) {
      if (isValidBackdoorAdjustment(graph, treatment, outcome, subset)) {
        validSets.push(subset);
      }
    }
  }

  return validSets;
}

/**
 * Generate backdoor adjustment formula
 */
export function generateBackdoorFormula(
  treatment: string,
  outcome: string,
  adjustmentSet: string[],
): AdjustmentFormula {
  const zList = adjustmentSet.length > 0 ? adjustmentSet.join(", ") : "";
  const sumOver = adjustmentSet.length > 0 ? `\\sum_{${zList}}` : "";

  let latex: string;
  let plainText: string;

  if (adjustmentSet.length === 0) {
    latex = `P(${outcome} | do(${treatment})) = P(${outcome} | ${treatment})`;
    plainText = `P(${outcome}|do(${treatment})) = P(${outcome}|${treatment})`;
  } else {
    latex = `P(${outcome} | do(${treatment})) = ${sumOver} P(${outcome} | ${treatment}, ${zList}) P(${zList})`;
    plainText = `P(${outcome}|do(${treatment})) = Σ_{${zList}} P(${outcome}|${treatment},${zList}) P(${zList})`;
  }

  return {
    adjustmentSet,
    latex,
    plainText,
    type: "backdoor",
    isValid: true,
  };
}

// ============================================================================
// FRONTDOOR CRITERION
// ============================================================================

/**
 * Check if frontdoor criterion is satisfied
 */
export function checkFrontdoorCriterion(
  graph: CausalGraph,
  treatment: string,
  outcome: string,
): { satisfied: boolean; mediators: string[] } {
  // Find potential mediators (variables on directed path from X to Y)
  const mediators = findMediators(graph, treatment, outcome);

  if (mediators.length === 0) {
    return { satisfied: false, mediators: [] };
  }

  // Check frontdoor conditions for each mediator set
  for (const m of mediators) {
    // Condition 1: M intercepts all directed paths from X to Y
    if (!interceptsAllDirectedPaths(graph, treatment, outcome, [m])) {
      continue;
    }

    // Condition 2: No unblocked backdoor path from X to M
    // Check that X -> M path is unconfounded when we intervene on X
    const mutilatedForXM = createMutilatedGraph(graph, [
      { variable: treatment, value: 0, type: "atomic" },
    ]);
    checkDSeparation(graph, { x: [treatment], y: [m], z: [] }); // Validates path exists
    checkDSeparation(mutilatedForXM, { x: [treatment], y: [m], z: [] }); // Validates no backdoor after intervention

    // Condition 3: All backdoor paths from M to Y are blocked by X
    const backdoorMY = checkDSeparation(graph, {
      x: [m],
      y: [outcome],
      z: [treatment],
    });

    if (backdoorMY.separated) {
      return { satisfied: true, mediators: [m] };
    }
  }

  return { satisfied: false, mediators: [] };
}

/**
 * Find potential mediators between treatment and outcome
 */
function findMediators(
  graph: CausalGraph,
  treatment: string,
  outcome: string,
): string[] {
  const mediators: string[] = [];
  const descendants = getDescendants(graph, treatment);
  const ancestors = getAncestors(graph, outcome);

  for (const nodeId of descendants) {
    if (nodeId !== outcome && ancestors.has(nodeId)) {
      mediators.push(nodeId);
    }
  }

  return mediators;
}

/**
 * Check if mediator set intercepts all directed paths
 */
function interceptsAllDirectedPaths(
  graph: CausalGraph,
  treatment: string,
  outcome: string,
  mediators: string[],
): boolean {
  const mediatorSet = new Set(mediators);

  function dfs(current: string, visited: Set<string>): boolean {
    if (current === outcome) {
      return false; // Found path that doesn't go through mediator
    }
    if (mediatorSet.has(current) && current !== treatment) {
      return true; // Path goes through mediator
    }

    for (const edge of graph.edges) {
      if (edge.from === current && !visited.has(edge.to)) {
        visited.add(edge.to);
        if (!dfs(edge.to, visited)) {
          return false;
        }
        visited.delete(edge.to);
      }
    }

    return true;
  }

  return dfs(treatment, new Set([treatment]));
}

/**
 * Generate frontdoor adjustment formula
 */
export function generateFrontdoorFormula(
  treatment: string,
  outcome: string,
  mediator: string,
): AdjustmentFormula {
  const latex = `P(${outcome} | do(${treatment})) = \\sum_{${mediator}} P(${mediator} | ${treatment}) \\sum_{${treatment}'} P(${outcome} | ${mediator}, ${treatment}') P(${treatment}')`;
  const plainText = `P(${outcome}|do(${treatment})) = Σ_${mediator} P(${mediator}|${treatment}) Σ_{${treatment}'} P(${outcome}|${mediator},${treatment}') P(${treatment}')`;

  return {
    adjustmentSet: [mediator],
    latex,
    plainText,
    type: "frontdoor",
    isValid: true,
  };
}

// ============================================================================
// INSTRUMENTAL VARIABLES
// ============================================================================

/**
 * Find an instrumental variable
 */
export function findInstrumentalVariable(
  graph: CausalGraph,
  treatment: string,
  outcome: string,
): string | null {
  for (const node of graph.nodes) {
    if (node.id === treatment || node.id === outcome) continue;

    // Check IV conditions:
    // 1. Z affects X (relevance)
    const affectsX = graph.edges.some(
      (e) => e.from === node.id && e.to === treatment,
    );
    if (!affectsX) continue;

    // 2. Z does not directly affect Y (exclusion)
    const affectsYDirectly = graph.edges.some(
      (e) => e.from === node.id && e.to === outcome,
    );
    if (affectsYDirectly) continue;

    // 3. Z is independent of confounders (exogeneity)
    // Simplified check: Z has no common ancestors with Y except through X
    const mutilated = createMutilatedGraph(graph, [
      { variable: treatment, value: 0, type: "atomic" },
    ]);
    const independentOfY = checkDSeparation(mutilated, {
      x: [node.id],
      y: [outcome],
      z: [],
    });
    if (independentOfY.separated) {
      return node.id;
    }
  }

  return null;
}

/**
 * Generate instrumental variable formula
 */
export function generateIVFormula(
  treatment: string,
  outcome: string,
  instrument: string,
): AdjustmentFormula {
  const latex = `\\beta_{${treatment} \\to ${outcome}} = \\frac{Cov(${outcome}, ${instrument})}{Cov(${treatment}, ${instrument})}`;
  const plainText = `β_${treatment}→${outcome} = Cov(${outcome},${instrument}) / Cov(${treatment},${instrument})`;

  return {
    adjustmentSet: [instrument],
    latex,
    plainText,
    type: "instrumental",
    isValid: true,
  };
}

// ============================================================================
// GENERAL IDENTIFIABILITY (DO-CALCULUS RULES)
// ============================================================================

/**
 * Apply Rule 1 of do-calculus: Insertion/deletion of observations
 * P(y|do(x),z,w) = P(y|do(x),w) if (Y ⊥⊥ Z | X, W)_{G_{\overline{X}}}
 */
export function applyRule1(
  graph: CausalGraph,
  y: string[],
  x: string[],
  z: string[],
  w: string[],
): { applicable: boolean; result?: string } {
  const mutilated = createMutilatedGraph(
    graph,
    x.map((v) => ({ variable: v, value: 0, type: "atomic" as const })),
  );

  const separation = checkDSeparation(mutilated, {
    x: y,
    y: z,
    z: [...x, ...w],
  });

  if (separation.separated) {
    return {
      applicable: true,
      result: `P(${y.join(",")}|do(${x.join(",")}),${[...w].join(",")})`,
    };
  }

  return { applicable: false };
}

/**
 * Apply Rule 2 of do-calculus: Action/observation exchange
 * P(y|do(x),do(z),w) = P(y|do(x),z,w) if (Y ⊥⊥ Z | X, W)_{G_{\overline{X}\underline{Z}}}
 */
export function applyRule2(
  graph: CausalGraph,
  y: string[],
  x: string[],
  z: string[],
  w: string[],
): { applicable: boolean; result?: string } {
  // Create graph with incoming edges to X removed and outgoing edges from Z removed
  let modified = createMutilatedGraph(
    graph,
    x.map((v) => ({ variable: v, value: 0, type: "atomic" as const })),
  );

  // Also remove outgoing edges from Z
  const zSet = new Set(z);
  modified = {
    ...modified,
    edges: modified.edges.filter((e) => !zSet.has(e.from)),
  };

  const separation = checkDSeparation(modified, {
    x: y,
    y: z,
    z: [...x, ...w],
  });

  if (separation.separated) {
    return {
      applicable: true,
      result: `P(${y.join(",")}|do(${x.join(",")}),${z.join(",")},${w.join(",")})`,
    };
  }

  return { applicable: false };
}

/**
 * Apply Rule 3 of do-calculus: Insertion/deletion of actions
 * P(y|do(x),do(z),w) = P(y|do(x),w) if (Y ⊥⊥ Z | X, W)_{G_{\overline{X}\overline{Z(W)}}}
 */
export function applyRule3(
  graph: CausalGraph,
  y: string[],
  x: string[],
  z: string[],
  w: string[],
): { applicable: boolean; result?: string } {
  // Remove incoming edges to X and to Z (where Z has no descendants in W)
  const modified = createMutilatedGraph(
    graph,
    [...x, ...z].map((v) => ({
      variable: v,
      value: 0,
      type: "atomic" as const,
    })),
  );

  const separation = checkDSeparation(modified, {
    x: y,
    y: z,
    z: [...x, ...w],
  });

  if (separation.separated) {
    return {
      applicable: true,
      result: `P(${y.join(",")}|do(${x.join(",")}),${w.join(",")})`,
    };
  }

  return { applicable: false };
}

/**
 * Check general identifiability using do-calculus
 * (simplified version - full algorithm is more complex)
 */
function checkGeneralIdentifiability(
  graph: CausalGraph,
  treatment: string,
  outcome: string,
): { identifiable: boolean; formula?: string } {
  // Check if there are any bidirected edges (latent confounders)
  const hasBidirected = graph.edges.some((e) => e.type === "bidirected");

  if (!hasBidirected) {
    // Without latent confounders, effect is always identifiable
    return {
      identifiable: true,
      formula: `P(${outcome}|do(${treatment}))`,
    };
  }

  // Check if treatment and outcome share a bidirected path
  const bidirectedConnected = areBidirectedConnected(graph, treatment, outcome);

  if (!bidirectedConnected) {
    // No direct latent confounding
    return {
      identifiable: true,
      formula: `P(${outcome}|do(${treatment}))`,
    };
  }

  return { identifiable: false };
}

/**
 * Check if two nodes are connected via bidirected edges
 */
function areBidirectedConnected(
  graph: CausalGraph,
  node1: string,
  node2: string,
): boolean {
  const bidirectedAdj = new Map<string, string[]>();

  for (const node of graph.nodes) {
    bidirectedAdj.set(node.id, []);
  }

  for (const edge of graph.edges) {
    if (edge.type === "bidirected") {
      bidirectedAdj.get(edge.from)?.push(edge.to);
      bidirectedAdj.get(edge.to)?.push(edge.from);
    }
  }

  const visited = new Set<string>();
  const queue = [node1];
  visited.add(node1);

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === node2) return true;

    for (const neighbor of bidirectedAdj.get(current) || []) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    }
  }

  return false;
}

// ============================================================================
// INTERVENTION ANALYSIS
// ============================================================================

/**
 * Analyze the effect of an intervention
 */
export function analyzeIntervention(
  graph: CausalGraph,
  request: InterventionRequest,
): InterventionResult {
  const treatment = request.interventions[0]?.variable;
  const outcome = request.outcomes[0];

  if (!treatment || !outcome) {
    return {
      originalDistribution: { type: "continuous" },
      interventionDistribution: null,
      identifiable: false,
      nonIdentifiableReason: "Missing treatment or outcome variable",
    };
  }

  // Check identifiability
  const identResult = isIdentifiable(graph, treatment, outcome);

  if (!identResult.identifiable) {
    return {
      originalDistribution: { type: "continuous" },
      interventionDistribution: null,
      identifiable: false,
      nonIdentifiableReason: identResult.reason,
    };
  }

  // Generate adjustment formula based on method
  let adjustment: AdjustmentFormula | undefined;

  if (identResult.method === "backdoor") {
    const backdoorSet = findBackdoorAdjustmentSet(graph, treatment, outcome);
    if (backdoorSet) {
      adjustment = generateBackdoorFormula(treatment, outcome, backdoorSet);
    }
  } else if (identResult.method === "frontdoor") {
    const frontdoorResult = checkFrontdoorCriterion(graph, treatment, outcome);
    if (frontdoorResult.satisfied && frontdoorResult.mediators.length > 0) {
      adjustment = generateFrontdoorFormula(
        treatment,
        outcome,
        frontdoorResult.mediators[0],
      );
    }
  } else if (identResult.method === "instrumental") {
    const iv = findInstrumentalVariable(graph, treatment, outcome);
    if (iv) {
      adjustment = generateIVFormula(treatment, outcome, iv);
    }
  }

  return {
    originalDistribution: { type: "continuous" },
    interventionDistribution: { type: "continuous" },
    identifiable: true,
    estimand: adjustment?.latex,
    adjustment,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getDescendants(graph: CausalGraph, nodeId: string): Set<string> {
  const descendants = new Set<string>();
  const stack = [nodeId];

  while (stack.length > 0) {
    const current = stack.pop()!;
    for (const edge of graph.edges) {
      if (edge.from === current && !descendants.has(edge.to)) {
        descendants.add(edge.to);
        stack.push(edge.to);
      }
    }
  }

  return descendants;
}

function getAncestors(graph: CausalGraph, nodeId: string): Set<string> {
  const ancestors = new Set<string>();
  const stack = [nodeId];

  while (stack.length > 0) {
    const current = stack.pop()!;
    for (const edge of graph.edges) {
      if (edge.to === current && !ancestors.has(edge.from)) {
        ancestors.add(edge.from);
        stack.push(edge.from);
      }
    }
  }

  return ancestors;
}

function* getCombinations(arr: string[], k: number): Generator<string[]> {
  if (k === 0) {
    yield [];
    return;
  }
  for (let i = 0; i <= arr.length - k; i++) {
    for (const rest of getCombinations(arr.slice(i + 1), k - 1)) {
      yield [arr[i], ...rest];
    }
  }
}
